import React, { useState, useEffect, useCallback } from 'react';
import { GripVertical, Eye, EyeOff, ArrowUp, ArrowDown, LayoutGrid, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { toast } from 'sonner';
import { projectBoardsAPI } from '../lib/api';
import { CARD_FIELD_REGISTRY, DEFAULT_VISIBLE } from '../pages/ProjectsList';

/**
 * CardLayoutConfig — slide-over panel for managing which fields appear on kanban cards.
 *
 * Props:
 *   open        — boolean
 *   onOpenChange — (open: boolean) => void
 *   board       — { id, name, card_layout: { visible_fields, field_order } }
 *   onSaved     — () => void  (refresh boards after save)
 */
export function CardLayoutConfig({ open, onOpenChange, board, onSaved }) {
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // Initialize from board config
  useEffect(() => {
    if (!board) return;
    const layout = board.card_layout || { visible_fields: DEFAULT_VISIBLE, field_order: DEFAULT_VISIBLE };
    const visible = new Set(layout.visible_fields || DEFAULT_VISIBLE);
    const ordered = layout.field_order || layout.visible_fields || DEFAULT_VISIBLE;

    // Build ordered list: visible fields first (in order), then hidden fields
    const allKeys = Object.keys(CARD_FIELD_REGISTRY);
    const orderedVisible = ordered.filter(k => allKeys.includes(k));
    const remaining = allKeys.filter(k => !orderedVisible.includes(k));

    setFields([
      ...orderedVisible.map(key => ({ key, visible: visible.has(key) })),
      ...remaining.map(key => ({ key, visible: visible.has(key) })),
    ]);
  }, [board]);

  const toggleField = (key) => {
    // Don't allow hiding job_name — it's always required
    if (key === 'job_name') return;
    setFields(prev => prev.map(f => f.key === key ? { ...f, visible: !f.visible } : f));
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const target = index + direction;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setFields(newFields);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const visibleFields = fields.filter(f => f.visible).map(f => f.key);
      const fieldOrder = fields.map(f => f.key);
      await projectBoardsAPI.updateCardLayout(board.id, { visible_fields: visibleFields, field_order: fieldOrder });
      toast.success('Card layout saved');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save layout');
    } finally { setSaving(false); }
  };

  const resetToDefault = () => {
    const allKeys = Object.keys(CARD_FIELD_REGISTRY);
    const defaultSet = new Set(DEFAULT_VISIBLE);
    setFields([
      ...DEFAULT_VISIBLE.map(key => ({ key, visible: true })),
      ...allKeys.filter(k => !defaultSet.has(k)).map(key => ({ key, visible: false })),
    ]);
  };

  const visibleCount = fields.filter(f => f.visible).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="card-layout-sheet">
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <LayoutGrid className="w-4 h-4" /> Card Layout
          </SheetTitle>
          <SheetDescription>
            Customize which fields appear on kanban cards for <span className="font-medium text-foreground">{board?.name}</span>.
          </SheetDescription>
        </SheetHeader>

        <div className="text-xs text-muted-foreground mb-3 flex items-center justify-between">
          <span>{visibleCount} field{visibleCount !== 1 ? 's' : ''} visible</span>
          <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={resetToDefault} data-testid="reset-card-layout">
            Reset to default
          </Button>
        </div>

        <div className="space-y-1" data-testid="card-layout-fields">
          {fields.map((field, index) => {
            const meta = CARD_FIELD_REGISTRY[field.key];
            if (!meta) return null;
            const isLocked = field.key === 'job_name';

            return (
              <div
                key={field.key}
                className={`flex items-center gap-2 px-2 py-2 rounded-lg border transition-colors ${
                  field.visible ? 'bg-background border-border' : 'bg-muted/30 border-transparent opacity-60'
                }`}
                data-testid={`card-field-${field.key}`}
              >
                <GripVertical className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{meta.label}</p>
                  {meta.description && <p className="text-[11px] text-muted-foreground truncate">{meta.description}</p>}
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => moveField(index, -1)}
                    disabled={index === 0}
                    className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    data-testid={`move-up-${field.key}`}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => moveField(index, 1)}
                    disabled={index === fields.length - 1}
                    className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    data-testid={`move-down-${field.key}`}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <Switch
                    checked={field.visible}
                    onCheckedChange={() => toggleField(field.key)}
                    disabled={isLocked}
                    className="ml-1"
                    data-testid={`toggle-${field.key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex gap-2">
          <Button onClick={handleSave} disabled={saving} className="flex-1" data-testid="save-card-layout">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Save Layout
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="px-4">
            Cancel
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
